import { useState } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Icon, Spinner } from '@joor/design-system'
import styled from 'styled-components'

import { encodeURIComponentImageName, getSizedImageUrl } from 'utils/images'

import { GALLERY_IMAGE_DIMENSION } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.constants'
import { ProductImagesIds } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.ids'
import { ProductImage as ProductImageType } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.types'
import useStore from 'features/Linesheets/Modals/ManageImagesModal/store/useManageImagesStore'

const ImageContainer = styled.span`
  position: relative;

  &:hover span {
    opacity: 1;
    z-index: 9999;
  }
`

const Image = styled.img`
  width: 55px;
  height: 88px;
  border: 1px dashed var(--color-primary-200);
  border-radius: 4px;
  object-fit: cover;
  cursor: grab;

  &.dragging {
    cursor: grabbing;
    border: 1px solid var(--color-secondary-800);
  }
`

const Loader = styled(Spinner)`
  position: absolute;
  top: 40%;
  left: 35%;

  & circle {
    stroke: var(--color-secondary-600);
  }
`

const RemoveIcon = styled(Icon)`
  position: absolute;
  top: -8%;
  right: -8%;
  cursor: pointer;
  opacity: 0;
  z-index: -9999;

  svg {
    background-color: white;
    border-radius: 50%;
  }

  &.disabled svg {
    border: 1px solid var(--color-primary-200);
  }

  &:not(.disabled) svg {
    border: 1px solid var(--color-primary-800);
    &:hover {
      --main-color: var(--color-error-500);
      border: 1px solid var(--color-error-500);
    }
  }
`

type ProductImageProps = {
  image: ProductImageType
  areActionsDisabled: boolean
}

const ProductImage = ({ image, areActionsDisabled }: ProductImageProps) => {
  const componentId =
    (image.unmatchedUploadId as string) || (image.id as string)
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: componentId })
  const productImages = useStore((state) => state.productImages)
  const setProductImages = useStore((state) => state.setProductImages)
  const [isLoading, setIsLoading] = useState(true)

  const handleRemoveImage = (): void => {
    if (areActionsDisabled) {
      return
    }
    const newProductImages = [...productImages]
    const index = productImages.findIndex(
      (img) =>
        (img.unmatchedUploadId &&
          image.unmatchedUploadId &&
          img.unmatchedUploadId === image.unmatchedUploadId) ||
        img.id === image.id,
    )
    if (index < 0) {
      return
    }
    newProductImages.splice(index, 1)
    setProductImages({ productImages: newProductImages })
  }

  return (
    <ImageContainer
      data-testid={`${ProductImagesIds.Image}__${componentId}`}
      {...attributes}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Image
        {...listeners}
        className={isDragging ? 'dragging' : ''}
        src={getSizedImageUrl(
          encodeURIComponentImageName(image.url),
          GALLERY_IMAGE_DIMENSION,
        )}
        onLoad={() => setIsLoading(false)}
      />
      {isLoading && <Loader />}
      <RemoveIcon
        id={`${ProductImagesIds.RemoveButton}__${componentId}`}
        data-testid={`${ProductImagesIds.RemoveButton}__${componentId}`}
        {...(areActionsDisabled
          ? { className: 'disabled', variant: 'DISABLED' }
          : { variant: 'DEFAULT' })}
        iconName="close"
        iconSize="small"
        onClick={handleRemoveImage}
      />
    </ImageContainer>
  )
}

export default ProductImage
